import { useEffect } from 'react';
import { Camera, FileText, Images } from 'lucide-react';
import './AttachMenu.css';

/**
 * Everything that can be attached, behind one button.
 *
 * There used to be three — photos, a document, the camera — sitting in the bar
 * next to each other, which is three decisions before the first one anybody
 * actually makes. This is that decision made once, and it is nothing but the
 * decision: three options of equal weight, one tap, gone.
 *
 * The camera used to be *live* here, a running viewfinder in the first tile. It
 * is a screen of its own now (see CameraScreen), and the reason is that the tile
 * had run out of room rather than that it was wrong: a flash, a flip and a
 * choice between a photograph and a clip do not fit into 148 pixels beside the
 * picture they are meant to be framing. What is left behind is the accent tone
 * on this one option, which is what says that most photographs sent from a phone
 * are taken to be sent.
 *
 * «Галерея» is the system picker rather than a grid of ours: a page cannot
 * enumerate a camera roll, only receive what somebody hands it (see the README's
 * note on the media handoff), and what it hands back lands on the compose
 * screen.
 */
export function AttachMenu({
  onCamera,
  onGallery,
  onFile,
  onClose,
}: {
  /** Opens the camera screen — a photograph or a clip, filmed here. */
  onCamera: () => void;
  /** Opens the system picker for photos and videos. */
  onGallery: () => void;
  /** Opens the system picker for anything at all. */
  onFile: () => void;
  onClose: () => void;
}) {
  // Escape closes it, as it closes every other sheet in the app.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className="attach-scrim" onClick={onClose} />

      <div className="attach-menu" role="dialog" aria-label="Вложение">
        <button type="button" className="attach-option attach-option-camera" onClick={onCamera}>
          <span className="attach-icon">
            <Camera size={22} />
          </span>
          Камера
        </button>
        <button type="button" className="attach-option" onClick={onGallery}>
          <span className="attach-icon">
            <Images size={22} />
          </span>
          Галерея
        </button>
        <button type="button" className="attach-option" onClick={onFile}>
          <span className="attach-icon">
            <FileText size={20} />
          </span>
          Файл
        </button>
      </div>
    </>
  );
}
